import { SectionName } from "@/entities/dossier-settings";
import { RoleGate, UserRole } from "@/features/auth";
import { BerichtBalk } from "@/features/berichten";
import { sectionConfigMap, useDossierId } from "@/features/dossier-details";
import { ErrorDisplay, Icon } from "@/shared/components";
import { useIsScrolled } from "@/shared/hooks/use-is-scrolled";
import { cn } from "@/shared/utils";
import { type UseQueryResult } from "@tanstack/react-query";
import { PropsWithChildren, type ReactNode, useMemo } from "react";
import { useNavigate } from "react-router";
import { DossierScrollContext, useDossierScrollDetails } from "./dossier-scroll-details";

type DossierDetailsHeaderProps = {
    section: SectionName;
    showBackButton?: boolean;
    showBerichtBalk?: boolean;
    className?: string;
    onBackClick?: () => void;
    title?: string; // only pass when the default section title needs to be overruled
};

export function DossierDetailsHeader({
    section,
    showBackButton,
    className,
    onBackClick,
    title,
    children,
    showBerichtBalk = false,
}: Readonly<PropsWithChildren<DossierDetailsHeaderProps>>) {
    const dossierId = useDossierId();
    const { title: sectionTitle, onderwerp } = sectionConfigMap[section];
    const { isScrolled } = useDossierScrollDetails();
    const navigate = useNavigate();

    const handleBackClick = () => {
        if (onBackClick) {
            onBackClick();
        } else {
            void navigate("..");
        }
    };

    return (
        <div className={isScrolled.top ? "shadow-elevated-2" : ""}>
            <hgroup className={cn("text-content-emphasis bg-elevated-1 sticky top-0 z-10 grid gap-1 p-2 px-4 pt-3 font-medium", className)}>
                {showBackButton && (
                    <button onClick={handleBackClick} className="text-content-interactive-default flex w-fit items-center gap-1.5">
                        <Icon name="arrow-left" />
                        <span>vorige</span>
                    </button>
                )}
                <div className="flex flex-row items-center justify-between">
                    {(title ?? sectionTitle) && <h1 className="text-xxl font-bold">{title ?? sectionTitle}</h1>}
                    {children}
                </div>
            </hgroup>
            <RoleGate roles={[UserRole.REGIEVOERDER, UserRole.SUPPORTCONSULTANT, UserRole.ALLEENLEZEN]}>
                {showBerichtBalk && onderwerp && <BerichtBalk dossierId={dossierId} onderwerp={onderwerp} />}
            </RoleGate>
        </div>
    );
}

interface DossierDetailsContentProps extends React.ComponentProps<"div"> {
    className?: string;
    children?: ReactNode;
}

export function DossierDetailsContent({ className, children, ...props }: Readonly<DossierDetailsContentProps>) {
    const { ref } = useDossierScrollDetails();

    return (
        <div
            className={cn("[scrollbar-width:thin] [scrollbar-gutter:stable] overflow-y-auto px-4 pt-2 pb-4", className)}
            ref={ref}
            {...props}
        >
            {children}
        </div>
    );
}

interface DossierDetailsPanelProps {
    children?: ReactNode;
    testId?: string;
}

export function DossierDetailsPanel({ children, testId = "dossier-details-panel" }: Readonly<DossierDetailsPanelProps>) {
    const { ref, isScrolled } = useIsScrolled();

    const contextValue = useMemo(() => ({ isScrolled, ref }), [isScrolled, ref]);

    return (
        <DossierScrollContext value={contextValue}>
            <div data-testid={testId} className="bg-elevated-1 relative flex max-h-full w-full flex-col rounded-l">
                {children}
            </div>
        </DossierScrollContext>
    );
}

type DossierDetailsViewProps<TData> = {
    section: SectionName;
    query: UseQueryResult<TData, Error>;
    skeleton: ReactNode;
    children: (data: TData) => ReactNode;
    showBackButton?: boolean;
    onBackClick?: () => void;
    title?: string;
    errorTitle?: string;
};

/**
 * Generic wrapper component that handles loading, error, and success states for dossier detail views.
 * Reduces boilerplate by abstracting the common pattern:
 * - Shows skeleton during loading
 * - Shows error with retry on failure
 * - Renders content on success
 *
 * @deprecated This component is deprecated. Use alternative implementation.
 *
 * @example
 * ```tsx
 * <DossierDetailsView
 *   title="Faciliteiten"
 *   query={faciliteiten}
 *   skeleton={<FaciliteitenSkeleton />}
 * >
 *   {(data) => <FaciliteitenContent data={data} />}
 * </DossierDetailsView>
 * ```
 */
export function DossierDetailsView<TData>({
    section,
    query,
    skeleton,
    children,
    showBackButton = true,
    onBackClick,
    title,
    errorTitle,
}: Readonly<DossierDetailsViewProps<TData>>) {
    const { data, isPending, error, refetch } = query;

    if (isPending) {
        return skeleton;
    }

    if (error) {
        return (
            <DossierDetailsPanel>
                <DossierDetailsHeader section={section} showBackButton={showBackButton} onBackClick={onBackClick} title={title} />
                <DossierDetailsContent>
                    <ErrorDisplay
                        error={error}
                        variant="section"
                        title={errorTitle ?? `Fout bij het laden van ${sectionConfigMap[section].title}`}
                        onRetry={() => void refetch()}
                    />
                </DossierDetailsContent>
            </DossierDetailsPanel>
        );
    }

    return <>{children(data)}</>;
}
